<template lang="pug">

  transition(name="fade" appear)

    .ui-arrow-arrow(
      ref='arrow' 
    )
      .copy(v-if="showCopy")

        .inner(v-if="showLoadingCopy")
          p {{ lang_en['home-loading'] }}
 
      button(
        @click="onClick"
        @touchstart="onTouchStart"
        @mouseenter="onMouseEnter"
        @focus="onFocus"
        @touchend="onTouchEnd"
        @mouseleave="onMouseLeave"
        @blur="onBlur"
        @mousedown="onPointerDown"
        @mouseup="onPointerUp"
        :aria-label="ariaCopy"
        )
        canvas( ref="canvas" )

</template>
<script>

  import { mapState, mapMutations } from 'vuex'
  import ResizeService from '@/services/ResizeService'
  import Raf from '@/helpers/Raf'
  import Env from '@/config/env'
  import { LOAD, MODAL_STATES, PATHS, EVENTS } from '@/config/app'
  import { DEG_TO_RAD, map } from '@/helpers/MathUtils'
  import gsap from 'gsap/all'
  import Communicator from '@/helpers/Communicator'

  export default {

    computed: {

      ...mapState('app', [
        'loading',
        'home',
        'motion_clamp',
        'reduced_motion',
        'modal_state'
      ]), 

      ctaCopy (){
        return Env.desktop ? this.lang_en['home-loading-scroll-cta'] :  this.lang_en['home-loading-swipe-cta']
      },

      showButton(){
        return this.modal_state !== MODAL_STATES.MENU
      } 
    },

    watch: {

      'loading.state'(val){
        if(val === LOAD.STATES.COMPLETE){ 

          this.showLoadingCopy = false
          this.showCta = true
          this.showArrow()

          gsap.to(this.tweenObj.bg, 2, {
            rotationSpeed: 0,   
            ease: 'power2.inOut',
            onComplete: () => {
              this.loadComplete = true
            }
          })
        }
      },

      '$route'(val){
        this.updateVisibility()
      },

      modal_state(val){
        this.updateVisibility()
      },

      'home.current_section'(val){
        if(val.index === 0){
          gsap.to(this.tweenObj, { 
            duration: (3 * this.motion_clamp),
            alpha: 1, 
            ease: 'quad.inOut' 
          })
          this.ariaCopy = this.lang_en['home-aria-label-button-cta']
        } else if(val.index  === (this.home.sections.length - 1)){
          //this.destoryArrow()
          this.ariaCopy = 'restart experience' 
          gsap.fromTo(this.tweenObj.replay, 2 * this.motion_clamp, {
            rotation: 0
          }, { 
            rotation: 360,
            ease: 'Power2.easeInOut'
          })

        } else if(val.index > 0){
          this.showCopy = false
          gsap.to(this.tweenObj, {
            duration: (3 * this.motion_clamp),
            alpha: 0.5, 
            ease: 'quad.inOut' 
          })
          this.ariaCopy = this.lang_en['home-aria-label-down-arrow-button']
        }

        this.ctaSection = (val && val.index === (this.home.sections.length - 1))

        gsap.to(this.tweenObj.replay, 2 * this.motion_clamp, {
          alpha: this.ctaSection ? 1 : 0,   
          ease: 'Power2.easeInOut'
        })
 
      }
    },

    data() { 
      return {
        ariaCopy: '',
        showCopy: true,
        showCta: false,
        showLoadingCopy: true
      }
    },

    mounted() {
      this.setup();
    },


    methods: {

      ...mapMutations('app', [
        'nextHomeSection',
        'setModalState'
      ]),

      setup(){

        this.STATES = {
          DOWN: 'STATE:DOWN',
          UP: 'STATE:UP'
        }
        
        this.loadComplete = false
        this.state = null
        this.prev_state = null
 
        this.lineWidth = 2 * Env.pixelRatio
        this.strokeStyle = '#9297FF'
        this.pixelScale = Env.pixelRatio
        this.canAnimate = false
   
        this.hideCompleteDuration = 1.1
        this.isFocused = false

        this.count = 1.5

        this.stroke = {
          length : Env.mobile ? (7 * Env.pixelRatio) : (9 * Env.pixelRatio)
        }

        this.arrow = {
          width: Env.mobile ? (9 * Env.pixelRatio) : (12 * Env.pixelRatio),
          height: Env.mobile ? (4 * Env.pixelRatio) : (6 * Env.pixelRatio),
          halfWidth : 0
        }

        this.tweenObj = {

          arc: 0,
          arcBlend: 0,
          arcTween: 1,

          radiusMult: 1,
 
          alpha: 0,
          arrow: {
            y: -100,
            sy: 0
          },
          bg: {
            theta: 0,
            rotationSpeed: 7,
            rotation: -(Math.PI * 2)
          },
          fg: {
            thetaMult: 0
          },
          hover: {
            lineWidth: 1, // mult
            color: '#FFFFFF',
            backgroundAlpha: 0,
            alpha: 0.5
          },

          replay: {
            alpha: 0,
            rotation: 0
          }
        }

        this.diameter = (50 * Env.pixelRatio)
        this.radius = (this.diameter * 0.5)

        this.$refs.canvas.width = this.diameter
        this.$refs.canvas.height = this.diameter
        this.ctx = this.$refs.canvas.getContext("2d")


        this.tweenObj.arrow.y = -100 * Env.pixelRatio
        this.arrow.halfWidth = (this.arrow.width * 0.5)

        this.onResize()
        this.show()
        this.addEvents()
      },


      addEvents(){
        Raf.add(this.onTick)
      },

      removeEvents(){
        Raf.remove(this.onTick)
      },

      ambientAnimation(){

        if(this.reduced_motion){
          return
        }

        let delay = 5

        this.animatingArrowIn = false
        this.animatingArrowOut = false

        gsap.delayedCall(delay, this.onAmbientDelay)
      },

      hideArrow(duration = 1.5){

        if (this.animatingArrowIn || this.animatingArrowOut) {
          return
        }

        this.animatingArrowOut = true

        gsap.killTweensOf([this.onAmbientDelay, this.showArrow, this.tweenObj.arrow])

        gsap.to(this.tweenObj.arrow, { 
          duration: (duration * this.motion_clamp),
          y: this.diameter,
          onUpdate: this.tick,
          ease: 'expo.inOut',
          // onComplete: this.showArrow
        })

        const completeDuration = Math.min(this.hideCompleteDuration, duration) * this.motion_clamp
        gsap.delayedCall(completeDuration, this.showArrow)
      },

      showArrow(){
        this.ariaCopy = this.lang_en['home-aria-label-button-cta']
        this.animatingArrowOut = false
        this.animatingArrowIn = true

        gsap.killTweensOf(this.tweenObj.arrow)
        gsap.fromTo(this.tweenObj.arrow, { 
          sy: -this.radius * 1.5,
          y: -this.radius * 1.5
        }, {
          duration: ( 2 * this.motion_clamp ), 
          sy: 0,
          y: 0,
          ease: 'expo.inOut',  
          onComplete: this.ambientAnimation
        })

        gsap.to(this.tweenObj, {
          duration: ( 1.8 * this.motion_clamp ), 
          arcBlend: 1,
          ease: 'expo.inOut'
        });
      },

      destoryArrow(){

        gsap.killTweensOf([this.onAmbientDelay, this.tweenObj.arrow])
        gsap.to(this.tweenObj.arrow, {
          duration: ( 1 * this.motion_clamp ), 
          y: this.diameter * 2,  
          ease: 'expo.inOut'
        })

        gsap.to(this.$refs.canvas, {
          duration: ( 1 * this.motion_clamp ), 
          scale: 1.2,
          alpha: 0,
          delay: 1,
          ease: 'expo.inOut',
          onComplete: () => {
            this.removeEvents()
          }
        })

      },

      tick(){

        this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height)
 
        this.drawBGcircle() 
        this.drawArrow()
        this.updateLoading()

      },

      updateVisibility(){
        if(this.$route.name === PATHS.HOME){
          if(this.modal_state === MODAL_STATES.MENU){
            this.hide()
          } else {
            this.show()
          }
        } else {
          this.hide()
        }
      },

      updateLoading(){ 
        this.tweenObj.bg.rotation += this.tweenObj.bg.rotationSpeed  
        this.count += ( this.tweenObj.bg.rotationSpeed * 0.002);
        var arc = Math.pow(Math.abs(Math.cos(this.count)), 2)
        this.tweenObj.bg.theta = map(this.tweenObj.arcBlend, 0, 1, arc, this.tweenObj.arcTween)
      },

      show(){

        gsap.to(this.tweenObj, {
          duration: ( 2 * this.motion_clamp ), 
          alpha: 1, 
          ease: 'quad.inOut' 
        })

        gsap.to(this.$el, {
          duration: ( 2 * this.motion_clamp ), 
          autoAlpha: 1, 
          ease: 'quad.inOut' 
        })

        // gsap.to(this.tweenObj.bg, 1.5, {
        //   delay: 1,
        //   ease: 'Quad.easeInOut'
        // })
      },

      hide(){
         gsap.to(this.$el, {
          duration: ( 0.5 * this.motion_clamp ), 
          autoAlpha: 0, 
          ease: 'expo.out' 
        })
      },

      mouseenter(){
        gsap.killTweensOf([this.tweenObj.hover, event.target])
        gsap.to(this.tweenObj.hover, {
          duration: ( 0.3 * this.motion_clamp ), 
          alpha: 0, 
          color: '#373C85',
          //lineWidth: 0.75, 
          backgroundAlpha: 1, 
          ease: 'expo.out' 
        })
        this.hideArrow(0.5)  

        gsap.to(event.target, {
          duration: ( 0.3 * this.motion_clamp ), 
          scale: 1.1,  
          ease: 'expo.out' 
        })

        Communicator.trigger(EVENTS.MOUSE_ENTER)
      },

      mouseleave(){
        gsap.killTweensOf([this.tweenObj.hover, event.target])
        gsap.to(this.tweenObj.hover, {
          duration: ( 0.5 * this.motion_clamp ),
          alpha: 0.5,
          color: '#FFFFFF',
          //lineWidth: 1,
          backgroundAlpha: 0, 
          ease: 'expo.inOut'
        })
        gsap.to(event.target, {
          duration: ( 1 * this.motion_clamp ), 
          scale: 1,  
          ease: 'expo.inOut' 
        })

        Communicator.trigger(EVENTS.MOUSE_LEAVE)
      },

      onAmbientDelay(){
        this.hideArrow()
      },

      onFocus(){
        this.isFocused = true
        this.onMouseEnter() 
      },

      onBlur(){
        this.isFocused = false
        this.onMouseLeave() 
      },

      onMouseEnter(event){
        if(Env.desktop){

          gsap.fromTo(this.tweenObj.replay, 1 * this.motion_clamp, {
            rotation: 0
          }, { 
            rotation: 360,
            ease: 'Power2.easeOut'
          })

          this.mouseenter()
        }
      },

      onMouseLeave(event){
        if(Env.desktop){
          this.mouseleave()
        }
      },

      onTouchStart(event){
        this.mouseenter()
      },

      onTouchEnd(event){
        this.mouseleave()
      },

      onClick(){
        this.nextHomeSection()
        this.setModalState( MODAL_STATES.NONE )
      },

      onPointerDown(){
        gsap.to(this.tweenObj, {
          duration: ( 0.3 * this.motion_clamp ),
          radiusMult : 0.75,
          ease: 'Expo.easeOut'
        })
      },

      onPointerUp(){
        gsap.to(this.tweenObj, {
          duration: ( 0.5 * this.motion_clamp ),
          radiusMult : 1,
          ease: 'Back.easeOut'
        })
      },

      onShowComplete(){
        this.ambientAnimation()
      },

      onTick(){
        this.tick();
      },

      onResize(){},

      updateScrollDirection(){
        this.scroll.direction = ( this.scroll.prev_top < this.scroll.top) ? 'down' : 'up'
      },

      drawReplayArrow(){
   
        let s = this.pixelScale 

        this.ctx.save()
        this.ctx.translate( 16 * s, 15 * s)

        this.ctx.save()
        
        this.ctx.globalAlpha = this.tweenObj.replay.alpha

        this.ctx.beginPath()

        let radius = this.radius 
        this.ctx.translate( 9 * s, 10 * s)
        this.ctx.rotate( this.tweenObj.replay.rotation * DEG_TO_RAD ) 
        this.ctx.translate( -9 * s, -10 * s )
        this.ctx.scale(s, s)


        this.ctx.moveTo(8.6  , 1.0)
        this.ctx.bezierCurveTo(13.7, 1.0, 17.8, 5.1, 17.8, 10.2)
        this.ctx.bezierCurveTo(17.8, 15.3, 13.6, 19.4, 8.6, 19.4)
        this.ctx.bezierCurveTo(5.6, 19.4, 2.9, 17.9, 1.2, 15.7)
        this.ctx.lineWidth = 2.0
        this.ctx.strokeStyle = this.tweenObj.hover.color 
        this.ctx.lineCap = "round"
        this.ctx.stroke()

        this.ctx.beginPath()
        this.ctx.moveTo(1.0, 18.9)
        this.ctx.lineTo(1.0, 15.5)
        this.ctx.lineTo(4.5, 15.5)
        this.ctx.lineJoin = "round"
        this.ctx.stroke()


        this.ctx.restore()

        
        this.ctx.restore() 
      },

      drawBGcircle(){

        let radius =  this.radius
        let radiusScaled =  this.radius * this.tweenObj.radiusMult
        let lineWidth = this.lineWidth * this.tweenObj.hover.lineWidth

        this.ctx.save()

        this.ctx.fillStyle = `rgba(255,255,255,${this.tweenObj.hover.backgroundAlpha})`
        this.ctx.fill()


        if(this.showLoadingCopy){  
          this.ctx.translate(radius, radius) 
          this.ctx.rotate(((this.tweenObj.bg.rotation)* DEG_TO_RAD)  + (this.count))
          this.ctx.translate(-radius, -radius) 
        }

        this.ctx.beginPath();

        let pulse = map(Math.sin(this.count),-1,1,0.5,1)
        this.ctx.globalAlpha = this.tweenObj.hover.alpha * this.tweenObj.alpha  * pulse

        this.ctx.strokeStyle = this.strokeStyle
        this.ctx.lineWidth = lineWidth
 
        this.ctx.arc( radius , radius , ( radiusScaled - lineWidth), 0, (this.tweenObj.bg.theta * 2) * Math.PI )

        this.ctx.stroke();
        this.ctx.restore()


      },
 

      drawArrow(){
 
        this.ctx.save()

      
        if( this.ctaSection ){
          this.drawReplayArrow()
        } else {

          let y = Math.round(this.radius - ((this.stroke.length + this.arrow.height) * 0.5)) + this.tweenObj.arrow.y

          let offset = {
            x: Math.round(this.radius - this.arrow.halfWidth),
            y: y
          }

          this.ctx.translate(offset.x, offset.y)

          this.ctx.beginPath()

          let lineWidth = this.lineWidth * this.tweenObj.hover.lineWidth
          this.ctx.lineWidth = lineWidth

          this.ctx.strokeStyle = this.tweenObj.hover.color 

          // arrow stem
          // this.ctx.fillStyle = this.tweenObj.hover.color
          this.ctx.moveTo(this.arrow.halfWidth, this.tweenObj.arrow.sy )
          this.ctx.lineTo(this.arrow.halfWidth, this.stroke.length + this.arrow.height)

          this.ctx.stroke()
          this.ctx.beginPath()
          this.ctx.lineCap = "round"
          this.ctx.lineJoin = "round"


          // arrow head
          this.ctx.lineTo(this.arrow.width, this.stroke.length)
          this.ctx.lineTo(this.arrow.halfWidth, this.stroke.length + this.arrow.height)
          this.ctx.lineTo(0, this.stroke.length)
          this.ctx.stroke() 

          this.ctx.translate(-offset.x, -offset.y)

        }

        // arrow mask
        this.ctx.globalCompositeOperation = 'destination-in';
        this.ctx.beginPath();
        this.ctx.arc(this.radius, this.radius, (this.radius - (2 * this.pixelScale)) + this.lineWidth, 0, (2 * Math.PI));
        this.ctx.fill();
        this.ctx.globalCompositeOperation = 'source-over'
        this.ctx.globalAlpha = this.tweenObj.alpha
        this.ctx.restore()

      } 
    }

  }
</script>

<style scoped lang="sass">

  @import "src/styles/config/variables.sass"

  $bp-desktop-size: 45px
  $bp-mobile-size: 45px

  .ui-arrow-arrow
    width: $bp-desktop-size
    height: $bp-desktop-size
    bottom: 3.5rem
    position: absolute
    right: 3.5rem
    margin-left: -30px
    transform: translate3d(0,0,0)
    z-index: 100
    opacity: 0
    visibility: hideCompleteDuration

    @media all and (max-width: $bp-mobile)
      width: $bp-mobile-size
      height: $bp-mobile-size
      bottom: 2rem
      right: 2rem

    .copy
      width: 220px
      height: 100%
      position: absolute
      margin-right: calc(1.5rem + #{$bp-desktop-size})
      right: 0
      top: 0

      @media all and (max-width: $bp-mobile)
        margin-right: calc(2rem + #{$bp-mobile-size})

      .inner
        position: absolute
        height: 100%
        width: 100%
        display: flex
        align-items: center

        p
          font-family: "Regular"
          position: absolute
          font-size: 12px
          text-align: right
          width: 100%

    button
      outline: none

    canvas
      width: 100%
      height: 100%
      cursor: pointer

      .mobile &
        transform: scale(-1)

</style>
